import { DocumentSnapshot, QueryDocumentSnapshot, QuerySnapshot, WhereFilterOp      }   from    '@firebase/firestore-types';
import { firebaseApp as firebase } from "./config.service";

const db                        =   firebase.firestore();

type sortType = "desc" | "asc" | undefined;

export function getRandomIDInCollection(collectionName: string): string {
    return firebase.firestore().collection(collectionName).doc().id;
}



// Level 1: Document Create, Update
export function pushDocument(collectionName: string, payload: any): Promise<firebase.firestore.DocumentReference> {
    return db.collection(collectionName).add(payload);
}

export function addDocument(collectionName: string, id: string, payload: any): Promise<any> {
    const path                  =   `${collectionName}/${id}`;
    return db.doc(path).set(payload, { merge: true} );
}

export function updateDocument(collectionName: string, id: string, payload: any): Promise<any> {
    const path                  =   `${collectionName}/${id}`;
    return db.doc(path).update(payload);
}

// Level 1: Retrieve
export async function fetchDocument(collectionName: string, id: string, classType: any): Promise<any> {
    const response              =   await db.collection(collectionName).doc(id).get();
    const classObject           =   new classType(response.data());
    return classObject;
}

export async function fetchDocumentRaw(collectionName: string, id: string): Promise<any> {
    const response              =   await db.collection(collectionName).doc(id).get();
    return response;
}

export async function fetchCollection(collectionName: string, queryLimit: number = 1000, classType: any): Promise<Array<any>> {
    const response              =   await db.collection(collectionName).orderBy('id').limit(queryLimit).get();
    return response.docs.map(doc => new classType(doc.data()));
}

export async function query1Collection(collectionName: string, prop: string, val: string, classType: any): Promise<Array<any>> {
    const response              =   await db.collection(collectionName).where(prop, '==', val).get();
    return response.docs.map(doc => new classType(doc.data()));
}

export async function query2CollectionCustomOp(collectionName: string, prop1: string, op1: WhereFilterOp, val1: any, prop2: string, op2: WhereFilterOp, val2: any, classType: any): Promise<Array<any>> {
    const response              =   await db.collection(collectionName).where(prop1, op1, val1).where(prop2, op2, val2).get();
    return response.docs.map(doc => new classType(doc.data()));
}


// Level 2: Document Create, Update
export function pushSubDocument(collection1Name: string, id1: string, collection2Name: string, payload: any): Promise<firebase.firestore.DocumentReference> {
    const path                  =   `${collection1Name}/${id1}/${collection2Name}`;
    return db.collection(path).add(payload);
}

export function addSubDocument(collection1Name: string, id1: string, collection2Name: string, id2: string, payload: any): Promise<any> {
    const path                  =   `${collection1Name}/${id1}/${collection2Name}/${id2}`;
    return db.doc(path).set(payload, { merge: true} );
}

export function updateSubDocument(collection1Name: string, id1: string, collection2Name: string, id2: string, payload: any): Promise<any> {
    const path                  =   `${collection1Name}/${id1}/${collection2Name}/${id2}`;
    return db.doc(path).update(payload);
}

export function deleteSubDocument(collection1Name: string, id1: string, collection2Name: string, id2: string): Promise<any> {
    const path                  =   `${collection1Name}/${id1}/${collection2Name}/${id2}`;
    return db.doc(path).delete();
}

// Level 2: Retrieve
export async function fetchSubDocumentPromise(collection1Name: string, id1: string, collection2Name: string, id2: string, classType: any): Promise<any> {
    const response              =   await db.collection(collection1Name).doc(id1).collection(collection2Name).doc(id2).get();
    const classObject           =   new classType(response.data());
    return classObject;
}

export function fetchSubDocument(collection1Name: string, id1: string, collection2Name: string, id2: string, classType: any, next: any) {
    db.collection(collection1Name).doc(id1).collection(collection2Name).doc(id2).onSnapshot({
        next: (snapshot: DocumentSnapshot) => next(new classType(snapshot.data()))
    });
}


export async function fetchSubDocumentRaw(collection1Name: string, id1: string, collection2Name: string, id2: string): Promise<any> {
    const response              =   await db.collection(collection1Name).doc(id1).collection(collection2Name).doc(id2).get();
    return response;
}

// export async function fetchSubCollection(collection1Name: string, doc1Id: string, collection2Name: string, classType: any): Promise<Array<any>> {
//     const response              =   await db.collection(collection1Name).doc(doc1Id).collection(collection2Name).get();
//     return response.docs.map(doc => new classType(doc.data()));
// }

export async function fetchSubCollection(collection1Name: string, doc1Id: string, collection2Name: string, classType: any, next: any) {
    db.collection(collection1Name).doc(doc1Id).collection(collection2Name).onSnapshot({
        next: (snapshot: QuerySnapshot) => next(snapshot.docs.map((doc: QueryDocumentSnapshot) => new classType(doc.data())))
    })
}

export function fetchSubCollectionRealTime(collection1Name: string, doc1Id: string, collection2Name: string, classType: any, next: any): any {
    db.collection(collection1Name).doc(doc1Id).collection(collection2Name).onSnapshot({
        next: (snapshot: QuerySnapshot) => next(snapshot.docs.map((doc: QueryDocumentSnapshot) => new classType(doc.data())))
    });
}

export async function fetchSubCollectionWith3Items(collection1Name: string, doc1Id: string, collection2Name: string, classType: any): Promise<Array<any>> {
    const response              =   await db.collection(collection1Name).doc(doc1Id).collection(collection2Name).orderBy('date', 'desc').limit(3).get();
    return response.docs.map(doc => new classType(doc.data()));
}

export async function fetchSubCollectionWithLimit(collection1Name: string, doc1Id: string, collection2Name: string, orderByField: string, sort: sortType, classType: any, limit: number = 100): Promise<Array<any>> {
    const response              =   await db.collection(collection1Name).doc(doc1Id).collection(collection2Name)
        .orderBy(orderByField, sort).limit(limit).get();

    return response.docs.map(doc => new classType(doc.data()));
}

export async function fetchSubCollectionWithOrder(path: string, orderBy: any[], limit: number = 100, classType: any) {
    const response              =   await db.collection(path).orderBy(orderBy[0], orderBy[1]).limit(limit).get();
    return response.docs.map(doc => new classType(doc.data()));
}

export async function query1SubCollectionWithLimit(collection1Name: string, doc1Id: string, collection2Name: string, prop: string, val: any, orderByField: string, sort: sortType, classType: any, limit: number = 100): Promise<Array<any>> {
    const response              =   await db.collection(collection1Name).doc(doc1Id).collection(collection2Name)
        .where(prop, '==', val)
        .orderBy(orderByField, sort).limit(limit).get();

    return response.docs.map(doc => new classType(doc.data()));
}

// export async function querySubCollectionWithLimitAndWithMonth(collection1Name: string, doc1Id: string, collection2Name: string, prop1: string, val1: any, prop2: string, val2: any, prop3: string, val3: any, orderByField: string, sort: sortType, classType: any, limit: number = 100): Promise<Array<any>> {
//     const response              =   await db.collection(collection1Name).doc(doc1Id).collection(collection2Name)
//         .where(prop1, '==', val1)
//         .where(prop2, '>=', val2)
//         .where(prop3, '<', val3)
//         .orderBy(orderByField, sort).limit(limit).get();

//     return response.docs.map(doc => new classType(doc.data()));
// }


export function querySubCollectionWithLimitAndWithMonth(collection1Name: string, doc1Id: string, collection2Name: string, prop1: string, val1: any, prop2: string, val2: any, prop3: string, val3: any, orderByField: string, sort: sortType, classType: any, next: any, limit: number = 100) {
    db.collection(collection1Name).doc(doc1Id).collection(collection2Name)
        .where(prop1, '==', val1)
        .where(prop2, '>=', val2)
        .where(prop3, '<', val3)
        .orderBy(orderByField, sort).limit(limit).onSnapshot({
            next: (snapshot: QuerySnapshot) => next(snapshot.docs.map((doc: QueryDocumentSnapshot) => new classType(doc.data())))
        });
}

// export async function getInvoicesByCustomer() {
//     const invoices = await db.collection('Users').doc('9384288287').collection('Invoices')
//           .where('customer.id', '==', '4gFl68ziBia9sfheoHnZ')
//           .where('date', '>=', new Date('2020-10'))
//           .where('date', '<', new Date('2020-1'))
//           .get();

//     console.log(invoices.docs.map(doc => doc.data()));
// }


